import ExportIcon from "@heroicons/react/24/solid/CircleStackIcon";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Theme } from "@mui/system";
import DynamicDataTable from "components/DynamicDataTable";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import { ERequestStatus } from "store/enums/index.enum";
import { setTableLoading } from "store/slicers/common";
import { RunAssistDashboard } from "store/slicers/dashboard";
import * as XLSX from "xlsx";

interface IFormData {
  search: string;
}

const AiAssistant = () => {
  const lgUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up("lg"));
  const [assistData, setAssistData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useAsyncDispatch();
  const t = useTranslation();

  const methods = useForm<IFormData>({
    defaultValues: {
      search: "",
    },
  });

  const { handleSubmit } = methods;

  const onExportScoreChartsData = () => {
    if (!assistData || assistData.length === 0) {
      return;
    }

    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Convert the data to a worksheet
    const worksheet = XLSX.utils.json_to_sheet(assistData);

    // Add the worksheet to the workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, "AI Assistant Results");

    // Generate filename with timestamp
    const timestamp = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, -5);
    const filename = `ai_assistant_results_${timestamp}.xlsx`;

    // Write and download the file
    XLSX.writeFile(workbook, filename);
  };

  const onRunAssist = async (formData) => {
    if (!formData.search.trim()) return;

    setIsLoading(true);
    await dispatch(setTableLoading(true));

    try {
      const { meta, payload } = await dispatch(
        RunAssistDashboard({ question: formData.search })
      );

      if (meta.requestStatus === ERequestStatus.FULFILLED && payload) {
        setAssistData(payload?.result);
      }
    } catch (error) {
      console.error("AI Assistant error:", error);
    } finally {
      setIsLoading(false);
      await dispatch(setTableLoading(false));
    }
  };

  const buttonDisabled = useMemo(() => {
    return !assistData;
  }, [assistData]);

  const { watch, setValue } = methods;
  const searchValue = watch("search");
  const canSubmit = searchValue?.trim() && !isLoading;

  return (
    <FormProvider {...methods}>
      <Box
        display="flex"
        alignItems={lgUp ? "center" : " left"}
        flexDirection={lgUp ? "row" : "column"}
        mb={2}
      >
        <Box
          flex={1}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            mb={1}
            variant="h4"
            fontWeight={500}
            color="text.secondary"
          >
            {t("ai_assistant")}
          </Typography>
          <Box>
            <Button
              variant="contained"
              onClick={onExportScoreChartsData}
              disabled={buttonDisabled}
              startIcon={<ExportIcon height={24} width={24} />}
            >
              <Typography>{t("export")}</Typography>
            </Button>
          </Box>
        </Box>
      </Box>
      {/* AI Assistant Tab */}
      <Box py={2}>
        <form onSubmit={handleSubmit(onRunAssist)}>
          <Paper
            elevation={1}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                borderColor: "primary.main",
                boxShadow: 2,
              },
              "&:focus-within": {
                borderColor: "primary.main",
                boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.12)",
              },
            }}
          >
            <TextField
              name="search"
              value={searchValue || ""}
              onChange={(e) => setValue("search", e.target.value)}
              placeholder={t("enter_your_question")}
              variant="outlined"
              fullWidth
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {isLoading ? (
                        <CircularProgress size={20} thickness={4} />
                      ) : (
                        <Button
                          type="submit"
                          disabled={!canSubmit}
                          variant="contained"
                          size="small"
                          sx={{
                            minWidth: "auto",
                            width: 32,
                            height: 32,
                            borderRadius: "50%",
                            backgroundColor: canSubmit
                              ? "primary.main"
                              : "action.disabled",
                            color: "white",
                            "&:hover": {
                              backgroundColor: canSubmit
                                ? "primary.dark"
                                : "action.disabled",
                            },
                          }}
                        >
                          <SendIcon sx={{ fontSize: 16 }} />
                        </Button>
                      )}
                    </Box>
                  </InputAdornment>
                ),
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiInputBase-input": {
                    px: 2,
                    fontSize: "1rem",
                    "&::placeholder": {
                      opacity: 0.7,
                    },
                  },
                },
              }}
            />
          </Paper>
        </form>

        {/* Loading State */}
        <Fade in={isLoading}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 3,
              gap: 2,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {t("ai_thinking") || "AI is thinking..."}
            </Typography>
          </Box>
        </Fade>
      </Box>

      {!isLoading && assistData && (
        <Box>
          <DynamicDataTable
            data={assistData}
            title={t("ai_assistant_results")}
          />
        </Box>
      )}
    </FormProvider>
  );
};

export default AiAssistant;
